"use client";

import { useSearchParams } from "next/navigation";
import { Suspense, useEffect, useState } from "react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  BarChart3,
  CheckCircle2,
  Globe,
  Home,
  Layers,
  PenSquare,
  TriangleAlert,
  XCircle,
} from "lucide-react";
import { useSession } from "@cartz/auth/client";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import Image from "next/image";
import { logout } from "@/components/logout-button";
import { cn, getFaviconUrl } from "@/utils/utils";
import {
  useOAuthClient,
  useOAuthUserShops,
  useSubmitOAuthConsent,
  useSwitchOAuthShop,
} from "./use-oauth-authorize";

type Status = "idle" | "submitting" | "success" | "denied" | "error";

const PERMISSIONS = [
  { icon: Home, label: "Your shop name, domain, and brand profile" },
  { icon: Layers, label: "Funnel and product catalog data" },
  { icon: BarChart3, label: "Analytics, revenue, and conversion metrics" },
  {
    icon: PenSquare,
    label: "Create and manage funnels, experiments, and optimizations",
  },
];

const getInitials = (value?: string | null) => {
  return value?.trim().charAt(0).toUpperCase() ?? "?";
};

const getOriginLabel = (origin?: string | null) => {
  if (!origin) {
    return "Unknown origin";
  }

  try {
    return new URL(origin).hostname;
  } catch {
    return origin;
  }
};

const ConnectionAvatar = ({
  icon,
  name,
}: {
  icon?: string | null;
  name?: string | null;
}) => {
  return (
    <Avatar className="size-10 rounded-lg border border-border/70 bg-muted shadow-sm">
      {icon ? <AvatarImage src={icon} alt={name ?? "Application"} /> : null}
      <AvatarFallback className="rounded-lg bg-muted text-sm font-semibold text-foreground">
        {getInitials(name)}
      </AvatarFallback>
    </Avatar>
  );
};

const CenteredCard = ({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) => {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4 py-4">
      <Card
        className={cn(
          "flex max-h-[calc(100vh-2rem)] w-full max-w-[500px] flex-col overflow-y-auto rounded-2xl px-4 pb-6 pt-6 shadow-shadow1 sm:px-8 sm:pb-8 sm:pt-8",
          className,
        )}
      >
        {children}
      </Card>
    </div>
  );
};

const PermissionRow = ({
  icon: Icon,
  label,
}: {
  icon: (typeof PERMISSIONS)[number]["icon"];
  label: string;
}) => {
  return (
    <div className="flex items-start gap-3 rounded-xl border border-border/60 bg-background/80 p-2.5">
      <div className="flex size-9 shrink-0 items-center justify-center rounded-xl bg-muted">
        <Icon className="size-4 text-muted-foreground" />
      </div>
      <p className="pt-0.5 text-sm leading-5 text-foreground">{label}</p>
    </div>
  );
};

const StateScreen = ({
  icon,
  title,
  description,
  status,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
  status: "success" | "denied" | "error";
}) => {
  return (
    <CenteredCard>
      <CardHeader className="items-center space-y-6 pb-2 text-center">
        <Image src="/new-jurni-logo.svg" alt="Jurni" width={100} height={30} />
        <div className="flex size-16 items-center justify-center rounded-2xl border border-border/70 bg-background shadow-sm">
          {icon}
        </div>
        <div className="space-y-2">
          <CardTitle className="text-2xl">{title}</CardTitle>
          <CardDescription className="mx-auto max-w-sm text-sm leading-6">
            {description}
          </CardDescription>
        </div>
      </CardHeader>
      {(status === "success" || status === "denied") && (
        <CardContent className="pt-2 text-center text-xs text-muted-foreground">
          Redirecting...
        </CardContent>
      )}
    </CenteredCard>
  );
};

function ConsentContent() {
  const searchParams = useSearchParams();
  const { data: session } = useSession();
  const [status, setStatus] = useState<Status>("idle");
  const [error, setError] = useState<string | null>(null);
  const [redirectURI, setRedirectURI] = useState<string | null>(null);
  const [selectedShopId, setSelectedShopId] = useState<number | null>(null);
  const [isSigningOut, setIsSigningOut] = useState(false);

  const consentCode = searchParams.get("consent_code");
  const clientId = searchParams.get("client_id");
  const { data: clientApp } = useOAuthClient(clientId);
  const { data: shopsData } = useOAuthUserShops();
  const switchShopMutation = useSwitchOAuthShop();
  const submitConsentMutation = useSubmitOAuthConsent();
  const shops = shopsData?.shops ?? [];
  const switchingShop = switchShopMutation.isPending;
  const clientAppIcon =
    clientApp?.icon ||
    (clientApp?.origin ? getFaviconUrl(clientApp.origin) : null);
  const selectedShop =
    selectedShopId !== null
      ? shops.find((shop) => shop.id === selectedShopId)
      : null;

  useEffect(() => {
    if (selectedShopId !== null) {
      return;
    }

    if (session?.session?.activeShopId) {
      setSelectedShopId(session.session.activeShopId);
      return;
    }

    if (shops.length > 0) {
      setSelectedShopId(shops[0].id);
    }
  }, [session?.session?.activeShopId, selectedShopId, shops]);

  useEffect(() => {
    if ((status === "success" || status === "denied") && redirectURI) {
      const timer = setTimeout(() => {
        window.location.href = redirectURI;
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [status, redirectURI]);

  const handleShopChange = async (value: string) => {
    const shopId = Number(value);
    setError(null);
    switchShopMutation.mutate(shopId, {
      onSuccess: () => {
        setSelectedShopId(shopId);
      },
      onError: (mutationError) => {
        setError(
          mutationError instanceof Error
            ? mutationError.message
            : "Failed to switch shop",
        );
      },
    });
  };

  const handleConsent = async (accept: boolean) => {
    if (!consentCode) return;

    setStatus("submitting");
    setError(null);

    submitConsentMutation.mutate(
      { accept, consentCode },
      {
        onSuccess: (result) => {
          if (!accept) {
            if (result.redirectURI) {
              setRedirectURI(result.redirectURI);
            }
            setStatus("denied");
            return;
          }

          if (result.redirectURI) {
            setRedirectURI(result.redirectURI);
            setStatus("success");
            return;
          }

          setError("Failed to process consent");
          setStatus("error");
        },
        onError: (mutationError) => {
          setError(
            mutationError instanceof Error
              ? mutationError.message
              : "Something went wrong. Please try again.",
          );
          setStatus("error");
        },
      },
    );
  };

  const handleSwitchAccount = async () => {
    setError(null);
    setIsSigningOut(true);

    try {
      await logout(window.location.pathname + window.location.search);
    } catch (logoutError) {
      setError(
        logoutError instanceof Error
          ? logoutError.message
          : "Unable to sign out. Please try again.",
      );
    } finally {
      setIsSigningOut(false);
    }
  };

  if (!consentCode || !clientId) {
    return (
      <StateScreen
        icon={<TriangleAlert className="size-7 text-amber-600" />}
        title="Invalid request"
        description="This authorization request is missing required parameters. Please restart the connection flow from the app you were using."
        status="error"
      />
    );
  }

  if (status === "success") {
    return (
      <StateScreen
        icon={<CheckCircle2 className="size-7 text-green-600" />}
        title="Authorization successful"
        description="Access was granted and the requesting app is being redirected back to complete setup."
        status="success"
      />
    );
  }

  if (status === "denied") {
    return (
      <StateScreen
        icon={<XCircle className="size-7 text-muted-foreground" />}
        title="Authorization denied"
        description="No data was shared with the requesting app. You can close this window or return and try again later."
        status="denied"
      />
    );
  }

  return (
    <CenteredCard>
      <CardHeader className="items-center space-y-3 pb-4 pt-0 text-center">
        <div className="flex items-center justify-center gap-3">
          <Image
            src="/new-jurni-icon-logo.svg"
            alt="Jurni"
            width={32}
            height={32}
            className="rounded-lg"
          />
          <div className="flex items-center gap-1">
            <span className="size-1.5 rounded-full bg-muted-foreground/30" />
            <span className="size-1.5 rounded-full bg-muted-foreground/30" />
            <span className="size-1.5 rounded-full bg-muted-foreground/30" />
          </div>
          <ConnectionAvatar icon={clientAppIcon} name={clientApp?.name} />
        </div>

        <CardTitle className="text-xl font-bold leading-snug">
          <span className="font-bold">
            {clientApp?.name ?? "An application"}
          </span>{" "}
          wants to access
          {"\n"}your Jurni workspace
        </CardTitle>
      </CardHeader>

      <CardContent className="space-y-3 p-0">
        {selectedShopId !== null && (
          <div className="rounded-2xl border bg-muted/20 p-4">
            <p className="mb-2 text-[11px] font-medium uppercase tracking-[0.16em] text-muted-foreground">
              Connecting to shop
            </p>
            {shops.length > 1 ? (
              <Select
                value={selectedShopId.toString()}
                onValueChange={handleShopChange}
                disabled={switchingShop}
              >
                <SelectTrigger className="h-11 rounded-xl bg-background">
                  <SelectValue placeholder="Select a shop" />
                </SelectTrigger>
                <SelectContent>
                  {shops.map((shop) => (
                    <SelectItem key={shop.id} value={shop.id.toString()}>
                      {shop.name || shop.domain}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <p className="text-sm font-medium text-foreground">
                {selectedShop?.name || selectedShop?.domain || "Your shop"}
              </p>
            )}
          </div>
        )}

        <div className="rounded-2xl border p-4">
          <p className="mb-3 text-[11px] font-medium uppercase tracking-[0.16em] text-muted-foreground">
            {clientApp?.name ?? "This application"} will have access to:
          </p>

          <div className="space-y-2.5">
            {PERMISSIONS.map(({ icon, label }) => (
              <PermissionRow key={label} icon={icon} label={label} />
            ))}
          </div>

          <Separator className="my-3" />

          <div className="flex items-start gap-3">
            <div className="flex size-9 shrink-0 items-center justify-center rounded-xl bg-muted">
              <Globe className="size-4 text-muted-foreground" />
            </div>
            <div className="min-w-0">
              <p className="text-xs text-muted-foreground">
                Application origin
              </p>
              <p className="mt-1 break-all text-sm font-medium text-foreground">
                {getOriginLabel(clientApp?.origin)}
              </p>
            </div>
          </div>
        </div>

        {error && (
          <Alert
            variant="destructive"
            className="rounded-2xl border-destructive/30 bg-destructive/5"
          >
            <TriangleAlert className="size-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <div className="space-y-2 pt-1">
          <Button
            className="h-11 w-full rounded-xl"
            disabled={submitConsentMutation.isPending || switchingShop}
            onClick={() => handleConsent(true)}
          >
            {submitConsentMutation.isPending
              ? "Authorizing..."
              : "Allow access"}
          </Button>
          <Button
            variant="outline"
            className="h-11 w-full rounded-xl"
            disabled={submitConsentMutation.isPending || switchingShop}
            onClick={() => handleConsent(false)}
          >
            Cancel
          </Button>
        </div>
      </CardContent>

      {session?.user?.email && (
        <div className="pt-4 text-center text-xs text-muted-foreground">
          <p>Logged in as {session.user.email}</p>
          <Button
            variant="link"
            className="mt-0.5 h-auto text-xs text-muted-foreground"
            disabled={isSigningOut}
            onClick={() => void handleSwitchAccount()}
          >
            {isSigningOut ? "Signing out..." : "Switch accounts"}
          </Button>
        </div>
      )}
    </CenteredCard>
  );
}

export const OAuthConsentScreen = () => {
  return (
    <Suspense>
      <ConsentContent />
    </Suspense>
  );
};
